import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate } from '@/lib/utils-helpers';
import { DollarSign, TrendingUp, Receipt, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function VendorEarnings() {
  const [earnings, setEarnings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [earningsRes, txnRes] = await Promise.all([
          api.get('/vendor/earnings'),
          api.get('/vendor/transactions', { params: { page, limit: 15 } })
        ]);
        setEarnings(earningsRes.data);
        setTransactions(txnRes.data.transactions || []);
        setTotalPages(txnRes.data.pages || txnRes.data.page || 1);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [page]);

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-1 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32" />)}</div></div>;

  // Handle both response formats
  const totalEarnings = earnings?.all_time || earnings?.total_earnings || 0;
  const todayEarnings = earnings?.today || 0;
  const weekEarnings = earnings?.this_week || 0;
  const monthEarnings = earnings?.this_month || 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">Earnings & Payouts</h1>
        <p className="text-muted-foreground mt-1">Track your revenue and transaction history.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">All-Time</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-50 text-emerald-600"><DollarSign className="h-4 w-4" /></div>
            </div>
            <p className="text-2xl font-semibold tabular-nums" data-font="display">{formatCurrency(totalEarnings)}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">Today</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600"><TrendingUp className="h-4 w-4" /></div>
            </div>
            <p className="text-2xl font-semibold tabular-nums" data-font="display">{formatCurrency(todayEarnings)}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">This Week</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-50 text-amber-600"><Receipt className="h-4 w-4" /></div>
            </div>
            <p className="text-2xl font-semibold tabular-nums" data-font="display">{formatCurrency(weekEarnings)}</p>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-muted-foreground">This Month</span>
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50 text-purple-600"><DollarSign className="h-4 w-4" /></div>
            </div>
            <p className="text-2xl font-semibold tabular-nums" data-font="display">{formatCurrency(monthEarnings)}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-medium">Transaction History</CardTitle></CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead><tr className="border-b text-muted-foreground">
                  <th className="text-left py-3 px-2 font-medium">Type</th>
                  <th className="text-left py-3 px-2 font-medium">Description</th>
                  <th className="text-left py-3 px-2 font-medium">Amount</th>
                  <th className="text-left py-3 px-2 font-medium">Date</th>
                </tr></thead>
                <tbody>
                  {transactions.map(txn => (
                    <tr key={txn.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-3 px-2"><Badge variant="outline" className="text-xs">{(txn.type || '').replace(/_/g, ' ')}</Badge></td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">{txn.description || txn.vendor_name || '—'}</td>
                      <td className="py-3 px-2 font-medium tabular-nums">{formatCurrency(txn.amount)}</td>
                      <td className="py-3 px-2 text-muted-foreground">{formatDate(txn.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="text-center py-8 text-muted-foreground">No transactions yet</p>}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
